import { useTranslation } from 'react-i18next';

import { ordinalX, useChartDirection } from './chart-utils';

export interface LineChartSeries {
  readonly id: string;
  readonly label: string;
  /** A CSS color, typically `var(--color-...)` — never a hardcoded hex. */
  readonly color: string;
  readonly values: readonly number[];
}

interface LineChartProps {
  /** Chronologically-ordered, compact bucket labels for the visual axis
   * (e.g. "16 Jul"); one per point in every series. */
  readonly labels: readonly string[];
  /** Full labels for the accessible data table only — e.g. the dual
   * Hijri/Gregorian string for each bucket. Falls back to `labels` when
   * omitted. Kept separate so the visual axis stays compact while the
   * screen-reader table still gets the full dual-calendar date
   * (instructions/04_UI_UX.md). */
  readonly fullLabels?: readonly string[];
  readonly series: readonly LineChartSeries[];
  /** Summarizes the whole chart for assistive tech (`role="img"`). */
  readonly ariaLabel: string;
  readonly height?: number;
}

const WIDTH = 480;
const PADDING = 24;

/**
 * A small multi-series time-series line chart. RTL-aware (earliest bucket at
 * the reading-start edge — right in Arabic, left in English), themed via CSS
 * tokens, and paired with a visually-hidden data table so the same numbers are
 * available to screen readers verbatim (instructions/04_UI_UX.md a11y).
 */
export function LineChart({
  labels,
  fullLabels,
  series,
  ariaLabel,
  height = 200,
}: LineChartProps): JSX.Element {
  const { t } = useTranslation();
  const dir = useChartDirection();
  const tableLabels = fullLabels ?? labels;

  if (labels.length === 0) {
    return <p className="chart__empty">{t('common.empty')}</p>;
  }

  const allValues = series.flatMap((s) => s.values);
  const max = Math.max(1, ...allValues);
  const innerWidth = WIDTH - PADDING * 2;
  const innerHeight = height - PADDING * 2;
  const xFor = (i: number) => PADDING + ordinalX(i, labels.length, innerWidth, dir);
  const yFor = (v: number) => PADDING + innerHeight - (v / max) * innerHeight;

  return (
    <figure className="chart chart--line">
      <svg
        role="img"
        aria-label={ariaLabel}
        viewBox={`0 0 ${WIDTH} ${height}`}
        className="chart__svg"
      >
        <line
          x1={PADDING}
          y1={PADDING + innerHeight}
          x2={WIDTH - PADDING}
          y2={PADDING + innerHeight}
          className="chart__axis"
        />
        {series.map((s) => (
          <path
            key={s.id}
            d={s.values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i)} ${yFor(v)}`).join(' ')}
            fill="none"
            stroke={s.color}
            strokeWidth={2}
            className="chart__line"
          />
        ))}
      </svg>
      <figcaption className="sr-only">
        <table>
          <caption>{ariaLabel}</caption>
          <thead>
            <tr>
              <th scope="col">{t('admin.insights.tableBucket')}</th>
              {series.map((s) => (
                <th scope="col" key={s.id}>
                  {s.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableLabels.map((label, i) => (
              <tr key={label}>
                <th scope="row">{label}</th>
                {series.map((s) => (
                  <td key={s.id}>{s.values[i] ?? 0}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </figcaption>
      {series.length > 1 && (
        <ul className="chart__legend">
          {series.map((s) => (
            <li key={s.id} className="chart__legendItem">
              <span className="chart__legendSwatch" style={{ background: s.color }} aria-hidden />
              {s.label}
            </li>
          ))}
        </ul>
      )}
    </figure>
  );
}
